import type { LucideIcon } from "lucide-react";
import {
  CalendarX,
  Receipt,
  ClipboardList,
  UserRound,
  FileText,
  Users,
  MessageSquareText,
  Laptop,
} from "lucide-react";

/* ────────────────────────────────────────────────────────────────────────
   Notification domain — SINGLE SOURCE OF TRUTH.

   Every notification concept (categories, the types under each category, how a
   category maps to a sidebar tab / route, and its label & icon) is declared
   here. Add or rename a category/type in THIS file and the badge counts, the
   sidebar red-dots and the notification panel all update automatically — no
   other file needs editing.
   ──────────────────────────────────────────────────────────────────────── */

// ── Categories ──────────────────────────────────────────────────────────────
export const NotificationCategory = {
  LEAVE: "LEAVE",
  REIMBURSEMENT: "REIMBURSEMENT",
  TIMESHEET: "TIMESHEET",
  PROFILE: "PROFILE",
  DOCUMENT: "DOCUMENT",
  ACCOUNT: "ACCOUNT",
  // CHANGE: new. The quarterly feedback contract adds a FEEDBACK badge category
  // delivered through the existing /api/notifications endpoints.
  FEEDBACK: "FEEDBACK",
  // CHANGE: new. Assets assigned to an employee, and the requests they raise
  // for one. Kept separate from INVENTORY-the-module because the badge is
  // about THIS person's asset, not the admin's stock list.
  ASSET: "ASSET",
} as const;
export type NotificationCategory =
  (typeof NotificationCategory)[keyof typeof NotificationCategory];

// ── Types (grouped by the category they belong to) ────────────────────────────
export const NotificationType = {
  // LEAVE
  LEAVE_APPROVAL_PENDING: "LEAVE_APPROVAL_PENDING",
  LEAVE_STATUS_CHANGED: "LEAVE_STATUS_CHANGED",
  LEAVE_DELETED: "LEAVE_DELETED",
  // REIMBURSEMENT
  REIMBURSEMENT_PENDING_APPROVAL: "REIMBURSEMENT_PENDING_APPROVAL",
  REIMBURSEMENT_STATUS_CHANGED: "REIMBURSEMENT_STATUS_CHANGED",
  REIMBURSEMENT_DELETED: "REIMBURSEMENT_DELETED",
  // TIMESHEET
  TIMESHEET_PENDING: "TIMESHEET_PENDING",
  // PROFILE
  PROFILE_INCOMPLETE: "PROFILE_INCOMPLETE",
  // DOCUMENT
  DOCUMENT_REQUIRED: "DOCUMENT_REQUIRED",
  DOCUMENT_REJECTED: "DOCUMENT_REJECTED",
  // CHANGE: new, and the only one of the five that is ADMIN-facing and
  // action-required — it stays until the documents are actually reviewed.
  DOCUMENT_REVIEW_PENDING: "DOCUMENT_REVIEW_PENDING",
  // ACCOUNT
  NEW_SIGNUP: "NEW_SIGNUP",
  ONBOARDING_REVIEW_PENDING: "ONBOARDING_REVIEW_PENDING",
  ACCOUNT_ACTIVATED: "ACCOUNT_ACTIVATED",
  NEW_USER_ADDED: "NEW_USER_ADDED",
  USER_DEACTIVATED: "USER_DEACTIVATED",
  // FEEDBACK
  FEEDBACK_REVIEW_PENDING: "FEEDBACK_REVIEW_PENDING",
  FEEDBACK_REVIEWED: "FEEDBACK_REVIEWED",
  FEEDBACK_RECEIVED: "FEEDBACK_RECEIVED",
  // ASSET
  ASSET_REQUEST_REJECTED: "ASSET_REQUEST_REJECTED",
  ASSET_DELETED: "ASSET_DELETED",
} as const;
export type NotificationType =
  (typeof NotificationType)[keyof typeof NotificationType];

// Maps every type to its owning category. This is the canonical relationship —
// category counts are derived from it, so it stays correct even if the backend
// groups items under a slightly different category key.
export const NOTIFICATION_TYPE_TO_CATEGORY: Record<NotificationType, NotificationCategory> = {
  [NotificationType.LEAVE_APPROVAL_PENDING]: NotificationCategory.LEAVE,
  [NotificationType.LEAVE_STATUS_CHANGED]: NotificationCategory.LEAVE,
  [NotificationType.LEAVE_DELETED]: NotificationCategory.LEAVE,
  [NotificationType.REIMBURSEMENT_PENDING_APPROVAL]: NotificationCategory.REIMBURSEMENT,
  [NotificationType.REIMBURSEMENT_STATUS_CHANGED]: NotificationCategory.REIMBURSEMENT,
  [NotificationType.REIMBURSEMENT_DELETED]: NotificationCategory.REIMBURSEMENT,
  [NotificationType.TIMESHEET_PENDING]: NotificationCategory.TIMESHEET,
  [NotificationType.PROFILE_INCOMPLETE]: NotificationCategory.PROFILE,
  [NotificationType.DOCUMENT_REQUIRED]: NotificationCategory.DOCUMENT,
  [NotificationType.DOCUMENT_REJECTED]: NotificationCategory.DOCUMENT,
  [NotificationType.DOCUMENT_REVIEW_PENDING]: NotificationCategory.DOCUMENT,
  [NotificationType.NEW_SIGNUP]: NotificationCategory.ACCOUNT,
  [NotificationType.ONBOARDING_REVIEW_PENDING]: NotificationCategory.ACCOUNT,
  [NotificationType.ACCOUNT_ACTIVATED]: NotificationCategory.ACCOUNT,
  [NotificationType.NEW_USER_ADDED]: NotificationCategory.ACCOUNT,
  [NotificationType.USER_DEACTIVATED]: NotificationCategory.ACCOUNT,
  // NOTE: without these three, the items are dropped from the panel while the
  // bell still counts them — a badge over an empty list.
  [NotificationType.FEEDBACK_REVIEW_PENDING]: NotificationCategory.FEEDBACK,
  [NotificationType.FEEDBACK_REVIEWED]: NotificationCategory.FEEDBACK,
  [NotificationType.FEEDBACK_RECEIVED]: NotificationCategory.FEEDBACK,
  [NotificationType.ASSET_REQUEST_REJECTED]: NotificationCategory.ASSET,
  [NotificationType.ASSET_DELETED]: NotificationCategory.ASSET,
};

// ── Category presentation + routing ───────────────────────────────────────────
export interface NotificationCategoryMeta {
  /** Human label shown in the notification panel. */
  label: string;
  /**
   * Sidebar route (must match a `url` in SideBarItems). Drives BOTH which tab
   * shows the red dot AND where clicking a notification navigates. Change here
   * to re-point a category to a different tab.
   */
  sidebarUrl: string;
  /**
   * Optional sub-tab (must match a tab label on the destination page, e.g. the
   * Profile page's "Profile" / "Documents" tabs). When set, clicking a
   * notification deep-links to this sub-tab and the sub-tab shows its own dot.
   */
  subTab?: string;
  icon: LucideIcon;
}

export const NOTIFICATION_CATEGORY_META: Record<NotificationCategory, NotificationCategoryMeta> = {
  [NotificationCategory.LEAVE]: { label: "Leave", sidebarUrl: "leaves", icon: CalendarX },
  [NotificationCategory.REIMBURSEMENT]: { label: "Reimbursement", sidebarUrl: "reimbursement", icon: Receipt },
  [NotificationCategory.TIMESHEET]: { label: "Timesheet", sidebarUrl: "timesheet", icon: ClipboardList },
  // Profile notifications open the Profile tab → "Profile" sub-tab.
  [NotificationCategory.PROFILE]: { label: "Profile", sidebarUrl: "profile", subTab: "Profile", icon: UserRound },
  // Document notifications open the Profile tab → "Documents" sub-tab.
  [NotificationCategory.DOCUMENT]: { label: "Documents", sidebarUrl: "profile", subTab: "Documents", icon: FileText },
  // Account notifications (sign-ups, onboarding review, activation, de-activation)
  // are admin-facing and surface on the Employees tab.
  [NotificationCategory.ACCOUNT]: { label: "Account", sidebarUrl: "employees", icon: Users },
  // Quarterly feedback. `sidebarUrl` must match SideBarItems' Feedback entry
  // ("feedback") or neither the red dot nor the click-through binds. No
  // `subTab`: QuarterlyFeedback's tabs are internal state, not labelled tabs
  // the ?subtab= convention can address.
  [NotificationCategory.FEEDBACK]: {
    label: "Feedback",
    sidebarUrl: "feedback",
    icon: MessageSquareText,
  },
  // Assets assigned to the viewer, and their own asset requests. Both live on
  // the Inventory tab, which SideBarItems grants to USER as well as the admin
  // roles — so an employee receiving these can actually reach the destination.
  [NotificationCategory.ASSET]: {
    label: "Assets",
    sidebarUrl: "inventories",
    icon: Laptop,
  },
};

// Per-type presentation + optional deep-link.
// - title: the notification headline (the API `message` becomes the body).
//   Falls back to the category label when a type has no entry.
// - queryParams: extra query appended to the category's destination route when
//   THIS specific type is clicked, e.g. ONBOARDING_REVIEW_PENDING opens the
//   Employees screen pre-filtered to onboarding users (?status=ONBOARDING).
export interface NotificationTypeMeta {
  title: string;
  queryParams?: Record<string, string>;
  /**
   * CHANGE: a type may now override its CATEGORY's destination.
   *
   * Needed because a category describes what a notification is ABOUT, not who
   * it is for. DOCUMENT_REVIEW_PENDING is a DOCUMENT notification — the badge
   * category the contract specifies — but it is addressed to an ADMIN about
   * SOMEBODY ELSE's documents, so sending it to the category default ("profile"
   * → the viewer's own Documents tab) would open the wrong person's records.
   *
   * NOTE: the derived selectors below resolve the destination per ITEM through
   * `getItemDestination`, so an override also moves the sidebar red dot and the
   * auto-mark-read behaviour, not just the click target. Setting only one of
   * the three would put the dot on a tab the click never opens.
   */
  sidebarUrl?: string;
  subTab?: string;
  /**
   * The query parameter the item's `subjectId` is sent as, e.g. "employeeId".
   * Omit for a type whose subject no longer exists at the destination — a
   * deleted leave or claim has nothing to open, so those navigate to the list.
   */
  subjectParam?: string;
}

/**
 * Where an item actually goes: its type override when there is one, otherwise
 * its category's default. Used by the click handler AND by the dot/auto-read
 * selectors so all three always agree.
 */
export const getItemDestination = (
  type: string,
  category: NotificationCategory,
): { sidebarUrl: string; subTab?: string } => {
  const typeMeta = NOTIFICATION_TYPE_META[type as NotificationType];
  const categoryMeta = NOTIFICATION_CATEGORY_META[category];
  return {
    sidebarUrl: typeMeta?.sidebarUrl ?? categoryMeta.sidebarUrl,
    subTab: typeMeta?.subTab ?? categoryMeta.subTab,
  };
};

export const NOTIFICATION_TYPE_META: Record<NotificationType, NotificationTypeMeta> = {
  [NotificationType.LEAVE_APPROVAL_PENDING]: { title: "Leave approval pending" },
  [NotificationType.LEAVE_STATUS_CHANGED]: { title: "Leave status updated" },
  // No subjectParam: the leave is gone, so there is nothing to open. The
  // subjectId still arrives on the item for traceability; the click lands on
  // the employee's own leave list, which is the contract's "UI should open".
  [NotificationType.LEAVE_DELETED]: { title: "Leave deleted" },
  [NotificationType.REIMBURSEMENT_PENDING_APPROVAL]: { title: "Reimbursement approval pending" },
  [NotificationType.REIMBURSEMENT_STATUS_CHANGED]: { title: "Reimbursement status updated" },
  // Same reasoning as LEAVE_DELETED — the claim no longer exists.
  [NotificationType.REIMBURSEMENT_DELETED]: { title: "Claim deleted" },
  [NotificationType.TIMESHEET_PENDING]: { title: "Timesheet pending" },
  [NotificationType.PROFILE_INCOMPLETE]: { title: "Complete your profile" },
  [NotificationType.DOCUMENT_REQUIRED]: { title: "Document required" },
  [NotificationType.DOCUMENT_REJECTED]: { title: "Document rejected" },
  /*
   * Admin-facing. Opens the Employees screen on THAT employee's Documents tab,
   * where an admin can approve or reject — not the viewer's own profile, which
   * is where the DOCUMENT category would otherwise send it.
   *
   * NOTE: action === true comes from the server, and getAutoReadTypesForSidebarUrl
   * deliberately skips action items, so this one survives a visit to the tab and
   * clears only when the documents are actually reviewed. That is the behaviour
   * the contract asks for; no extra code is needed to get it.
   */
  [NotificationType.DOCUMENT_REVIEW_PENDING]: {
    title: "Documents awaiting verification",
    sidebarUrl: "employees",
    subTab: "Documents",
    subjectParam: "employeeId",
  },
  [NotificationType.NEW_SIGNUP]: { title: "New sign-up" },
  // Admin clicks this → Employees screen filtered to onboarding users.
  [NotificationType.ONBOARDING_REVIEW_PENDING]: {
    title: "Onboarding awaiting review",
    queryParams: { status: "ONBOARDING" },
  },
  [NotificationType.ACCOUNT_ACTIVATED]: { title: "Account activated" },
  [NotificationType.NEW_USER_ADDED]: { title: "New team member added" },
  [NotificationType.USER_DEACTIVATED]: { title: "Team member deactivated" },
  // The server's `message` names the employee and the quarter, so these titles
  // stay generic rather than repeating it.
  [NotificationType.FEEDBACK_REVIEW_PENDING]: { title: "Goal sheet awaiting your approval" },
  [NotificationType.FEEDBACK_REVIEWED]: { title: "Your goal sheet was reviewed" },
  [NotificationType.FEEDBACK_RECEIVED]: { title: "New feedback for you" },
  // The request still exists (rejected, not removed), so it can be opened.
  // `tab` matches Invetories' own activeTab values: "assets" | "requests" |
  // "my-requests". An employee's own requests live under "my-requests".
  [NotificationType.ASSET_REQUEST_REJECTED]: {
    title: "Asset request rejected",
    queryParams: { tab: "my-requests" },
    subjectParam: "requestId",
  },
  // The asset is gone, so this opens the viewer's asset list rather than a
  // record that would 404.
  [NotificationType.ASSET_DELETED]: {
    title: "Asset removed",
    queryParams: { tab: "assets" },
  },
};

// Stable display order for the notification panel.
export const NOTIFICATION_CATEGORY_ORDER: NotificationCategory[] = [
  NotificationCategory.ACCOUNT,
  // FEEDBACK_REVIEW_PENDING is action-required, so the category sits high.
  NotificationCategory.FEEDBACK,
  NotificationCategory.LEAVE,
  NotificationCategory.REIMBURSEMENT,
  NotificationCategory.TIMESHEET,
  // DOCUMENT now carries an action-required admin type, so it moves above the
  // purely informational ASSET/PROFILE rows.
  NotificationCategory.DOCUMENT,
  NotificationCategory.ASSET,
  NotificationCategory.PROFILE,
];

// ── API response shape: GET /api/notifications/badge ──────────────────────────
export interface NotificationBadgeItem {
  type: NotificationType | string;
  count: number;
  action: boolean;
  message: string;
  /**
   * CHANGE: new. The id of the entity the notification is about — the employee
   * for DOCUMENT_REVIEW_PENDING, the asset request for ASSET_REQUEST_REJECTED,
   * and so on — so a click can open that record instead of a bare list.
   *
   * NOTE: optional, and typed to accept a number as well as a string. Every
   * existing type predates it and sends nothing, and an id that arrives as a
   * JSON number must not silently fail the lookup.
   */
  subjectId?: string | number | null;
}

export interface NotificationBadgeResponse {
  totalCount: number;
  // Keyed by category string (e.g. "LEAVE"). Left as `string` so an unexpected
  // key from the backend never breaks parsing.
  categories: Record<string, NotificationBadgeItem[]>;
}

// ── Derived selectors (pure, reused by hook / sidebar / panel) ────────────────

// Normalises a raw category key from the response to a known category (tolerant
// of pluralisation / casing, e.g. "ACCOUNTS" → ACCOUNT).
const coerceCategoryKey = (key: string): NotificationCategory | undefined => {
  const upper = (key || "").toUpperCase();
  if (upper in NOTIFICATION_CATEGORY_META) return upper as NotificationCategory;
  if (upper === "ACCOUNTS") return NotificationCategory.ACCOUNT;
  // Legacy key, kept so an older "EMPLOYEE(S)" payload still maps to Account.
  if (upper === "EMPLOYEE" || upper === "EMPLOYEES") return NotificationCategory.ACCOUNT;
  if (upper === "TIMESHEETS") return NotificationCategory.TIMESHEET;
  if (upper === "DOCUMENTS") return NotificationCategory.DOCUMENT;
  if (upper === "ASSETS") return NotificationCategory.ASSET;
  return undefined;
};

// Resolves an item's canonical category from its `type` (preferred), falling
// back to the response's category key.
const resolveItemCategory = (
  item: NotificationBadgeItem,
  fallbackKey: string,
): NotificationCategory | undefined =>
  NOTIFICATION_TYPE_TO_CATEGORY[item.type as NotificationType] ?? coerceCategoryKey(fallbackKey);

/** Total count across the whole payload (uses server total, falls back to a sum). */
export const getNotificationTotal = (res: NotificationBadgeResponse | null): number => {
  if (!res) return 0;
  if (typeof res.totalCount === "number") return res.totalCount;
  return Object.values(res.categories || {})
    .flat()
    .reduce((sum, item) => sum + (Number(item?.count) || 0), 0);
};

/** Per-category totals, robustly bucketed by each item's type. */
export const getCategoryCounts = (
  res: NotificationBadgeResponse | null,
): Record<NotificationCategory, number> => {
  const counts = Object.fromEntries(
    Object.values(NotificationCategory).map((c) => [c, 0]),
  ) as Record<NotificationCategory, number>;
  if (!res?.categories) return counts;

  Object.entries(res.categories).forEach(([key, items]) => {
    (items || []).forEach((item) => {
      const category = resolveItemCategory(item, key);
      if (category) counts[category] += Number(item?.count) || 0;
    });
  });
  return counts;
};

/**
 * Notification types for a sidebar route that should be auto-marked read when the
 * user opens that tab: INFORMATIONAL items only (action === false) with count > 0.
 *
 * Action-required items (action === true) are intentionally excluded — those need
 * the user to actually do something (e.g. approve a leave), so they must stay
 * flagged until resolved rather than clearing just because the tab was visited.
 *
 * Multiple categories can share one sidebarUrl (e.g. PROFILE + DOCUMENT → profile),
 * so types are collected across every category mapped to the given url.
 */
export const getAutoReadTypesForSidebarUrl = (
  res: NotificationBadgeResponse | null,
  sidebarUrl: string,
): string[] => {
  if (!res?.categories || !sidebarUrl) return [];
  const types = new Set<string>();
  Object.entries(res.categories).forEach(([key, items]) => {
    (items || []).forEach((item) => {
      const category = resolveItemCategory(item, key);
      if (!category) return;
      // Per ITEM, not per category: a type may override its destination.
      if (getItemDestination(String(item.type), category).sidebarUrl !== sidebarUrl) return;
      if (item.action === true) return; // needs explicit user action — keep it
      if ((Number(item?.count) || 0) <= 0) return;
      if (item.type) types.add(String(item.type));
    });
  });
  return Array.from(types);
};

/** Sidebar routes that should show a red dot (categories with count > 0). */
export const getFlaggedSidebarUrls = (res: NotificationBadgeResponse | null): Set<string> => {
  const urls = new Set<string>();
  if (!res?.categories) return urls;

  // CHANGE: iterates ITEMS rather than category totals, so a type that
  // overrides its destination dots the tab it actually opens. Bucketing by
  // category alone would have put the DOCUMENT_REVIEW_PENDING dot on the
  // admin's own Profile tab while the click opened Employees.
  Object.entries(res.categories).forEach(([key, items]) => {
    (items || []).forEach((item) => {
      const category = resolveItemCategory(item, key);
      if (!category) return;
      if ((Number(item?.count) || 0) <= 0) return;
      urls.add(getItemDestination(String(item.type), category).sidebarUrl);
    });
  });
  return urls;
};

/**
 * Sub-tab labels on a given page (sidebarUrl) that should show a dot — i.e. every
 * category mapped to that url whose count > 0 and that declares a `subTab`.
 * e.g. for "profile" → {"Profile"} and/or {"Documents"}.
 */
export const getFlaggedSubTabs = (
  res: NotificationBadgeResponse | null,
  sidebarUrl: string,
): Set<string> => {
  const subTabs = new Set<string>();
  if (!res?.categories) return subTabs;

  // Same per-item resolution as getFlaggedSidebarUrls.
  Object.entries(res.categories).forEach(([key, items]) => {
    (items || []).forEach((item) => {
      const category = resolveItemCategory(item, key);
      if (!category) return;
      if ((Number(item?.count) || 0) <= 0) return;
      const destination = getItemDestination(String(item.type), category);
      if (destination.sidebarUrl === sidebarUrl && destination.subTab) {
        subTabs.add(destination.subTab);
      }
    });
  });
  return subTabs;
};

// One category's grouped notifications, ready for the panel.
export interface NotificationGroup {
  category: NotificationCategory;
  meta: NotificationCategoryMeta;
  count: number;
  actionRequired: boolean;
  items: NotificationBadgeItem[];
}

/** Groups the payload by category (in display order) for the notification panel. */
export const getNotificationGroups = (
  res: NotificationBadgeResponse | null,
): NotificationGroup[] => {
  if (!res?.categories) return [];

  const buckets = new Map<NotificationCategory, NotificationBadgeItem[]>();
  Object.entries(res.categories).forEach(([key, items]) => {
    (items || []).forEach((item) => {
      const category = resolveItemCategory(item, key);
      if (!category) return;
      const bucket = buckets.get(category) ?? [];
      bucket.push(item);
      buckets.set(category, bucket);
    });
  });

  // Known categories first (in order), then any unexpected ones.
  const ordered = [
    ...NOTIFICATION_CATEGORY_ORDER,
    ...[...buckets.keys()].filter((c) => !NOTIFICATION_CATEGORY_ORDER.includes(c)),
  ];

  return ordered
    .map((category) => ({ category, items: buckets.get(category) }))
    .filter((entry): entry is { category: NotificationCategory; items: NotificationBadgeItem[] } =>
      Array.isArray(entry.items) && entry.items.length > 0,
    )
    .map(({ category, items }) => ({
      category,
      meta: NOTIFICATION_CATEGORY_META[category],
      count: items.reduce((sum, item) => sum + (Number(item?.count) || 0), 0),
      actionRequired: items.some((item) => item.action === true),
      items,
    }));
};

// A single, flattened notification ready to render as one list row: resolved
// title + message + count + its category icon/route.
export interface NotificationListItem {
  key: string;
  category: NotificationCategory;
  meta: NotificationCategoryMeta;
  type: string;
  title: string;
  message: string;
  count: number;
  action: boolean;
  /** Type-specific query params to apply at the destination (e.g. { status: "ONBOARDING" }). */
  query?: Record<string, string>;
  /**
   * Where this item opens — already resolved, so the bell never has to know
   * that a type can override its category. `meta` still supplies the icon and
   * the label, which stay the category's.
   */
  destination: { sidebarUrl: string; subTab?: string };
}

/**
 * Flat, de-duplicated list for the notification panel — one row per badge item,
 * action-required items first, then in category order.
 */
export const getNotificationItems = (
  res: NotificationBadgeResponse | null,
): NotificationListItem[] => {
  if (!res?.categories) return [];

  const items: NotificationListItem[] = [];
  Object.entries(res.categories).forEach(([key, arr]) => {
    (arr || []).forEach((item, index) => {
      const category = resolveItemCategory(item, key);
      if (!category) return;
      const meta = NOTIFICATION_CATEGORY_META[category];
      const typeMeta = NOTIFICATION_TYPE_META[item.type as NotificationType];

      // The subject id travels as a query param only when the type names one.
      // A deleted leave or claim has nothing left to open, so it is omitted
      // rather than pointing the destination at a record that is gone.
      const subject =
        typeMeta?.subjectParam && item.subjectId !== null && item.subjectId !== undefined
          ? { [typeMeta.subjectParam]: String(item.subjectId) }
          : undefined;

      items.push({
        key: `${category}-${item.type}-${index}`,
        category,
        meta,
        type: String(item.type),
        title: typeMeta?.title ?? meta.label,
        message: item.message,
        count: Number(item?.count) || 0,
        action: item.action === true,
        query:
          typeMeta?.queryParams || subject
            ? { ...(typeMeta?.queryParams ?? {}), ...(subject ?? {}) }
            : undefined,
        destination: getItemDestination(String(item.type), category),
      });
    });
  });

  const orderOf = (category: NotificationCategory) => {
    const index = NOTIFICATION_CATEGORY_ORDER.indexOf(category);
    return index === -1 ? Number.MAX_SAFE_INTEGER : index;
  };

  return items.sort(
    (a, b) => Number(b.action) - Number(a.action) || orderOf(a.category) - orderOf(b.category),
  );
};
